import React from 'react';

import * as Yup from 'yup';
import LinearProgress from '@mui/material/LinearProgress';
import { Form, Formik, FormikValues } from 'formik';

// import SSOWrapper from '../../components/auth/SSOWrapper';
// import ProviderBase from '../../components/auth/providerBase';
// import { SsoLoginLogo } from '../../components/auth/ssoLoginLogo';
import Button from '../../components/buttons/basicButton';
// import TextDivider from '../../components/display/text/textDivider';
import AuthError from '../../components/errors/AuthError';
import PasswordField from '../../components/inputs/PasswordField';
import TextInput from '../../components/inputs/textInput';
import NavLink from '../../components/nav/NavLink';
import styled from 'styled-components';

// constants
import aampeTheme from '../../constants/aampeTheme';

import useUserCreateMutation from '../../hooks/auth/useCreateUserMutation';
import useQueryParams from '../../hooks/helper/useQueryParams';
// hooks
import useInviteQuery from '../../hooks/invites/useInviteQuery';

// import Google from '../../images/google.svg?react';
import envConfig from '../../services/env-config';

// components
import BaseCard from './baseCard';
// helper
import { MIN_PASSWORD_LENGTH, PASSWORD_REGEX } from './helper';

const formSchema = Yup.object({
    customer: Yup.string().required('Required'),
    email: Yup.string().email('Must be a valid email').required('Required'),
    password: Yup.string()
        .required('Required')
        .min(MIN_PASSWORD_LENGTH, '8 chars minimum')
        .matches(PASSWORD_REGEX),
    passwordConfirmation: Yup.string().oneOf(
        [Yup.ref('password'), null],
        'Passwords must match',
    ),
});

const InviteCard: React.FC<React.PropsWithChildren<unknown>> = () => {
    const { invite: inviteCode } = useQueryParams();
    const { data, isPending: isLoading } = useInviteQuery(inviteCode);
    const createMutation = useUserCreateMutation();
    // const redirectUrl = window.location.origin; // redirect back to origin after login

    const handleSubmit = (values: FormikValues) => {
        createMutation.mutate({
            username: values.username,
            password: values.password,
            invite_code: inviteCode || '',
        });
    };

    return (
        <BaseCard
            title={createMutation.isSuccess ? 'Success!' : 'Setup your account'}
        >
            <Container>
                {createMutation.isError && (
                    <AuthError errorMsg={createMutation.error?.message ?? ''} />
                )}
                <LinearProgress
                    color="secondary"
                    style={{
                        visibility: isLoading ? 'visible' : 'hidden',
                        margin: aampeTheme.spacing(0),
                    }}
                />
                {!isLoading && !data && (
                    <Text>Sorry, we couldn't locate that invite code.</Text>
                )}
                {!!data && !createMutation.isSuccess && (
                    <>
                        {/*<SSOWrapper>*/}
                        {/*    <ProviderBase*/}
                        {/*        logo={*/}
                        {/*            <Google*/}
                        {/*                style={{*/}
                        {/*                    margin: 0,*/}
                        {/*                }}*/}
                        {/*            />*/}
                        {/*        }*/}
                        {/*        SSOProviderName="google"*/}
                        {/*        buttonText="Signup with Google"*/}
                        {/*        callbackUrl={redirectUrl}*/}
                        {/*    />*/}
                        {/*</SSOWrapper>*/}
                        {/*<SSOWrapper>*/}
                        {/*    <ProviderBase*/}
                        {/*        logo={<SsoLoginLogo />}*/}
                        {/*        SSOProviderName="microsoft-entra-id"*/}
                        {/*        buttonText="Signup with Entra ID"*/}
                        {/*        callbackUrl={redirectUrl}*/}
                        {/*        noWhiteBackground*/}
                        {/*    />*/}
                        {/*</SSOWrapper>*/}
                        {/*<TextDivider text="or signup with" />*/}
                        <Formik
                            initialValues={{
                                customer: data?.customer,
                                email: data?.email,
                                username: '',
                                password: '',
                                passwordConfirmation: '',
                                customer_id: data?.customer_id,
                            }}
                            validationSchema={formSchema}
                            onSubmit={handleSubmit}
                        >
                            {({
                                values,
                                handleChange,
                                handleBlur,
                                errors,
                                touched,
                            }) => (
                                <Form>
                                    <TextInput
                                        label="Company:"
                                        name="customer"
                                        isDisabled={true}
                                        value={values?.customer}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors?.email}
                                        touched={touched?.email}
                                        inputStyle={{
                                            width: '100%',
                                        }}
                                        wrapperStyle={{
                                            marginBottom: aampeTheme.spacing(1),
                                        }}
                                    />
                                    <TextInput
                                        label="Email:"
                                        name="email"
                                        isDisabled={true}
                                        value={values?.email}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors?.email}
                                        touched={touched?.email}
                                        inputStyle={{
                                            width: '100%',
                                        }}
                                        wrapperStyle={{
                                            marginBottom: aampeTheme.spacing(1),
                                        }}
                                    />
                                    <TextInput
                                        label="Username:"
                                        name="username"
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values?.username}
                                        error={errors?.username}
                                        touched={touched?.username}
                                        inputStyle={{
                                            width: '100%',
                                        }}
                                        wrapperStyle={{
                                            marginBottom: aampeTheme.spacing(1),
                                        }}
                                    />

                                    <PasswordField
                                        label="New password:"
                                        name="password"
                                        value={values.password}
                                        handleChange={handleChange}
                                        handleBlur={handleBlur}
                                        touched={touched?.password}
                                    />
                                    <PasswordField
                                        label="Confirm password:"
                                        name="passwordConfirmation"
                                        value={values.passwordConfirmation}
                                        handleChange={handleChange}
                                        handleBlur={handleBlur}
                                        touched={touched.passwordConfirmation}
                                        error={errors.passwordConfirmation}
                                        showValidationCheckList={false}
                                    />
                                    <StyledButton
                                        name="Create"
                                        type="submit"
                                        isPrimary
                                        isDisabled={createMutation.isPending}
                                        isLoading={createMutation.isPending}
                                        isSuccess={createMutation.isSuccess}
                                    />
                                </Form>
                            )}
                        </Formik>
                    </>
                )}
            </Container>
            <Footer>
                {createMutation.isSuccess
                    ? 'Your account was successfully created.'
                    : 'Already have an account?'}
                <Options>
                    <StyledNavLink
                        text="Login"
                        destination={envConfig.AUTH_SERVICE_SIGNIN}
                    />
                </Options>
            </Footer>
        </BaseCard>
    );
};

export default InviteCard;

const StyledButton = styled(Button)`
    width: 100%;
    margin: ${aampeTheme.spacing(2, 0)};
    font-size: 16px;
    font-weight: 600;
`;

const Container = styled.div({
    width: '100%',
});

const StyledNavLink = styled(NavLink)`
    font-size: 16px;
`;

const Footer = styled.div({
    display: 'flex',
    flexFlow: 'column nowrap',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    marginTop: aampeTheme.spacing(1),
});

const Options = styled.div({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: aampeTheme.spacing(1),
    marginTop: aampeTheme.spacing(2),
});

const Text = styled.div({
    width: '100%',
    textAlign: 'center',
});
